import { useEffect, useId, useState } from "react";
import { useTheme } from "@/lib/theme";

let seq = 0;

export function MermaidBlock({ source }: { source: string }) {
  const { isDark } = useTheme();
  const reactId = useId().replace(/[^a-zA-Z0-9]/g, "");
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const id = `mmd${reactId}x${++seq}`;
    setLoading(true);
    (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: isDark ? "dark" : "neutral",
          fontFamily: "inherit",
          themeVariables: { background: "transparent" },
        });
        const { svg } = await mermaid.render(id, source.trim());
        if (!cancelled) {
          setSvg(svg);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
          setSvg(null);
        }
      } finally {
        // mermaid leaves a scratch element behind on failure
        document.getElementById(`d${id}`)?.remove();
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, isDark, reactId]);

  if (error) {
    return (
      <figure className="rounded-xl border bg-card">
        <pre className="overflow-x-auto px-4 py-3 font-mono text-[0.8rem] leading-relaxed">
          <code>{source}</code>
        </pre>
        <figcaption className="border-t px-4 py-1.5 text-xs text-muted-foreground">
          diagram failed to render: {firstLine(error)}
        </figcaption>
      </figure>
    );
  }

  return (
    <figure className="mermaid-host overflow-x-auto rounded-xl border bg-card px-4 py-4">
      {svg ? (
        <div className="flex justify-center [&>svg]:max-w-full" dangerouslySetInnerHTML={{ __html: svg }} />
      ) : (
        <div className="h-24 animate-pulse rounded-md bg-muted/60" aria-busy={loading} />
      )}
    </figure>
  );
}

function firstLine(s: string): string {
  const l = s.split("\n").find((x) => x.trim()) ?? s;
  return l.length > 160 ? l.slice(0, 157) + "…" : l;
}
